import React, { useState, useEffect } from "react";
import { Modal, Tooltip } from "antd";
import "./Needs.css";
import { useNavigate } from "react-router-dom";
import { FileText, CheckCircle2, Eye, Search } from "lucide-react";
import { Panel } from "../components/ui";
import { formatDate } from "../utils/calendar.mjs";
import { frenchDate } from "../../utils/frenchDate";
import { needStatuses } from "../services/commercial.mjs";
export default function Needs({
  state,
  focusId,
  onCreateQuote,
  onSend,
  onSign,
  onRefuse,
  onStatus,
  onPayload,
}) {
  const navigate = useNavigate();
  const act = (action, ...args) => { setSelectedNeed(null); action(...args); };
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => {setPage(1);}, [filter, search, focusId, pageSize]);
  const needs = state.needs.filter(
    (n) =>
      (!focusId || n.id === focusId) &&
      (filter === "all" || n.status === filter) &&
      [state.clients.find(client => client.id === n.clientId)?.name, n.client, n.job, n.type, n.status, n.source].filter(Boolean).join(" ").toLocaleLowerCase("fr").includes(search.trim().toLocaleLowerCase("fr")),
  ).sort((a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0));
  const pageCount = Math.max(1,Math.ceil(needs.length/pageSize));
  const currentPage = Math.min(page,pageCount);
  const pagedNeeds = needs.slice((currentPage-1)*pageSize,currentPage*pageSize);
  return (
    <>
      <div className="commercialNeedsHeader">
      <Panel>
        <div className="taskFilters">
          <button
            aria-pressed={filter === "all"}
            className={filter === "all" ? "active" : ""}
            onClick={() => setFilter("all")}
          >
            Tous ({state.needs.length})
          </button>
          {needStatuses.map((status) => (
            <button
              key={status}
              aria-pressed={filter === status}
              className={filter === status ? "active" : ""}
              onClick={() => setFilter(status)}
            >
              {status} ({state.needs.filter((n) => n.status === status).length})
            </button>
          ))}
        </div>
        <p className="hint">
          Créez un devis complet avec prévisualisation PDF. Les devis enregistrés sont disponibles dans Devis.
        </p>
      </Panel>
      </div>
      <section className="commercialNeedsTableSection">
        <div className="commercialNeedsToolbar"><label><Search size={17} aria-hidden="true" /><input aria-label="Rechercher un besoin" placeholder="Rechercher un client, un métier…" value={search} onChange={event=>setSearch(event.target.value)} /></label><span>{needs.length} besoin{needs.length>1?"s":""}</span></div>
        <div className="commercialNeedsTableScroll">
          <table aria-label="Besoins et devis" data-responsive="custom" data-pagination="none">
            <thead><tr>{["Date","Source","Client / Contact","Métier recherché","Nombre","Type de contrat","Démarrage","Statut","Dernier devis","Actions"].map(label=><th key={label}>{label}</th>)}</tr></thead>
            <tbody>{pagedNeeds.map(need=>{
              const client=state.clients.find(client=>client.id===need.clientId);
              const contact=client?.contacts?.find(contact=>contact.id===need.contactId)||client?.contacts?.find(contact=>contact.active&&contact.primary);
              const quotes=state.quotes.filter(quote=>quote.needId===need.id).sort((a,b)=>b.version-a.version);
              const latest=quotes[0];
              return <tr key={need.id}>
                <td className="needsDate">{frenchDate(need.createdAt)}</td>
                <td>{need.source || <span className="text-slate-400">Non renseignée</span>}</td>
                <td><strong>{client?.name||need.client||"Client non renseigné"}</strong><small>{contact?.name||"Contact à rechercher"}</small></td>
                <td>{need.job||"À préciser"}<small>{need.urgency||""}</small></td>
                <td>{need.qty??"—"}</td><td>{need.type||"—"}</td><td className="needsDate">{formatDate(need.start)}</td>
                <td><span className={`needsStatus ${need.status==="signé"?"positive":need.status==="perdu"?"negative":"pending"}`}>{need.status||"à qualifier"}</span></td>
                <td>{latest?<><strong>{latest.quotationNumber?`N° ${latest.quotationNumber}`:`Version ${latest.version}`}</strong><small>{latest.status} · {quotes.length} version{quotes.length>1?"s":""}</small></>:<span className="text-slate-400">À créer</span>}</td>
                <td><div className="needsTableActions"><Tooltip title="Voir le besoin et les devis"><button type="button" aria-label={`Voir le besoin ${need.job}`} onClick={()=>setSelectedNeed(need.id)}><Eye size={17} /></button></Tooltip>
                  {need.status!=="signé"&&<Tooltip title={quotes.length?"Créer une nouvelle version du devis":"Créer le devis"}><button type="button" aria-label={`Créer le devis ${need.job}`} onClick={()=>act(onCreateQuote, need)}><FileText size={17} /></button></Tooltip>}
                </div></td>
              </tr>;
            })}
            {!needs.length&&<tr><td colSpan={10} className="needsTableEmpty">Aucun besoin pour ces filtres.</td></tr>}</tbody>
          </table>
        </div>
        <div className="needsTablePagination"><label>Lignes par page <select aria-label="Besoins par page" value={pageSize} onChange={event=>setPageSize(Number(event.target.value))}>{[10,25,50,100].map(size=><option key={size}>{size}</option>)}</select></label><span>{needs.length?(currentPage-1)*pageSize+1:0}–{Math.min(currentPage*pageSize,needs.length)} sur {needs.length}</span><button type="button" disabled={currentPage===1} onClick={()=>setPage(currentPage-1)}>Précédent</button><span>Page {currentPage} / {pageCount}</span><button type="button" disabled={currentPage===pageCount} onClick={()=>setPage(currentPage+1)}>Suivant</button></div>
      </section>
      <Modal open={!!selectedNeed} title="Détail du besoin" width={960} centered footer={null} styles={{body:{maxHeight:"75vh",overflowY:"auto"}}} onCancel={()=>setSelectedNeed(null)}>
      <div className="needsDetailRoot">
      {state.needs.filter(need=>need.id===selectedNeed).map((need) => {
        const client = state.clients.find((c) => c.id === need.clientId),
          contact =
            client?.contacts?.find((c) => c.id === need.contactId) ||
            client?.contacts?.find((c) => c.active && c.primary),
          quotes = state.quotes
            .filter((q) => q.needId === need.id)
            .sort((a, b) => b.version - a.version);
        return (
          <div key={need.id} className="needsDetail">
            <div className="needsDetailHeading">
              <div><span className="needsDetailEyebrow">{client?.name || need.client || "Client non renseigné"}</span><h2>{need.job || "Métier à préciser"}</h2><p>{need.qty ?? "—"} personne{Number(need.qty)>1?"s":""} · {need.type || "Contrat à préciser"}</p></div>
              <span className={`needsStatus ${need.status==="signé"?"positive":need.status==="perdu"?"negative":"pending"}`}>{need.status}</span>
            </div>
            <div className="needsDetailGrid">
              <section className="needsDetailSection"><h3>Le besoin</h3><dl className="needsDetailFacts">{[["Démarrage",frenchDate(need.start)],["Durée",need.duration],["Horaires",need.hours],["Urgence",need.urgency],["Créé le",frenchDate(need.createdAt)],["Source",need.source]].map(([label,value])=><div key={label}><dt>{label}</dt><dd>{value || "Non renseigné"}</dd></div>)}</dl></section>
              <section className="needsDetailSection"><h3>Contact client</h3><p className="needsContactName">{contact?.name || "Contact à rechercher"}</p>{contact?.role&&<p>{contact.role}</p>}{(contact?.phone||contact?.email)&&<div className="needsContactLinks">{contact.phone&&<a href={`tel:${contact.phone}`}>{contact.phone}</a>}{contact.email&&<a href={`mailto:${contact.email}`}>{contact.email}</a>}</div>}<p className="needsContactCompany">{client?.name || need.client}</p></section>
            </div>
            {(need.constraints || need.notes)&&<section className="needsDetailSection needsDetailNotes"><h3>Précisions</h3>{need.constraints&&<div><h4>Contraintes</h4><p>{need.constraints}</p></div>}{need.notes&&<div><h4>Commentaire</h4><p>{need.notes}</p></div>}</section>}
            <div className="needsDetailQuoteHeading"><div><h3>Devis associés</h3><p>{quotes.length} version{quotes.length>1?"s":""} enregistrée{quotes.length>1?"s":""}</p></div>
              {need.status !== "signé" && (
                <div className="needActions">
                  <button
                    className="primary"
                    onClick={() => act(onCreateQuote, need)}
                  >
                    <FileText />
                    {quotes.length
                      ? "Créer une nouvelle version"
                      : "Créer le devis"}
                  </button>
                  <label className="needsStatusControl">Statut du besoin<select
                    aria-label={`Statut du besoin ${need.job}`}
                    value={need.status}
                    onChange={(e) => onStatus(need.id, e.target.value)}
                  >
                    {needStatuses.map((status) => (
                      <option
                        key={status}
                        disabled={["signé", "devis envoyé"].includes(status)}
                      >
                        {status}
                      </option>
                    ))}
                  </select></label>
                </div>
              )}
            </div>
            {!quotes.length&&<div className="needsNoQuote"><FileText size={24} /><p>Aucun devis créé pour ce besoin.</p><span>Créez le premier devis depuis le bouton ci-dessus.</span></div>}
            <div className="needsQuoteList">{quotes.map((q) => (
              <div className="needsQuoteCard" key={q.id}>
                <div>
                  <b>Devis {q.quotationNumber ? `n° ${q.quotationNumber}` : `V${q.version}`}</b>{" "}
                  <span className="needsStatus pending">{q.status}</span>
                  <p className="muted">
                    Coefficient : {q.coefficient ?? "—"} · Taux :{" "}
                    {q.rate ?? "—"} € · Validité : {frenchDate(q.validUntil)}
                    <br />
                    Conditions : {q.conditions}
                  </p>
                  {q.refusalReason && (
                    <p className="error">Refus : {q.refusalReason}</p>
                  )}
                </div>
                <div className="needActions">
                  <button
                    className="secondary"
                    onClick={() => q.apiQuotationId ? navigate(`/devis?quote=${encodeURIComponent(q.apiQuotationId)}`) : act(onCreateQuote, need)}
                  >
                    {q.apiQuotationId ? "Prévisualiser / gérer le devis" : "Recréer au format devis admin"}
                  </button>
                  {!q.apiQuotationId && need.status !== "signé" &&
                    ["créé", "négociation"].includes(q.status) && (
                      <button className="primary" onClick={() => act(onSend, q.id)}>
                        Déclarer le devis envoyé
                      </button>
                    )}
                  {!q.apiQuotationId && need.status !== "signé" &&
                    ["envoyé", "négociation"].includes(q.status) && (
                      <button className="primary" onClick={() => act(onSign, q.id)}>
                        <CheckCircle2 />
                        Enregistrer la signature
                      </button>
                    )}
                  {!q.apiQuotationId && need.status !== "signé" && q.status !== "refusé" && (
                    <button className="secondary" onClick={() => act(onRefuse, q)}>
                      Devis refusé
                    </button>
                  )}
                </div>
              </div>
            ))}</div>
            {need.transmittedAt && (
              <div className="success">
                <b>🟢 DEVIS SIGNÉ · Opportunité gagnée</b>
                <p>
                  ✓ Besoin transmis automatiquement au recrutement le{" "}
                  {new Date(need.transmittedAt).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  .
                </p>
                {need.missingFields?.length > 0 && (
                  <p>
                    Informations à compléter : {need.missingFields.join(", ")}
                  </p>
                )}
                <button
                  className="secondary"
                  onClick={() => act(onPayload, need.id)}
                >
                  Voir les informations transmises
                </button>
              </div>
            )}
            <div className="needsDetailFooter"><button type="button" className="secondary" onClick={()=>setSelectedNeed(null)}>Fermer</button></div>
          </div>
        );
      })}
      </div>
      </Modal>
    </>
  );
}
